import clsx from 'clsx'
import { useState, useMemo, Fragment } from 'react'
import Lottie from 'lottie-react'
import LoaderAdm from '../../../assets/svgIcons/LoaderAdm.json'
import { useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'

// utils para formatear
import { formatCurrencySinSignoPeso, formatDMY } from '../../../components/utils/formatter'

// MUI (ícono para ordenar)
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'

// constants
import { DOC_MAP } from '../../../components/utils/constant'

// ui
import { Text } from '../../../components/modules/ui'

// columnas (nombres de header) para Libro Diario
import { colsLibroDiario as cols } from '../../../components/modules/data/headersTables'

export default function Table({
  rows,
  className,
  lastElementRef,
  hasNextPage,
  isFetchingNextPage,
  onLoadMore
}) {
  
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [order, setOrder] = useState(null)

  const orderableCols = [
    { col: 'Fecha', field: 'fecha' },
    { col: 'Glosa', field: 'glosa' },
    { col: 'Debe', field: 'debe' },
    { col: 'Haber', field: 'haber' }
  ]

  const findColConfig = col => orderableCols.find(item => item.col === col)

  const handleSort = ({ col, field }) => {
    if (!order) {
      setOrder({ col, field, direction: 'asc' })
      return
    }

    const isAsc = order.col === col && order.direction === 'asc'
    setOrder({ col, field, direction: isAsc ? 'desc' : 'asc' })
  }

  function getDocName(query) {
    const match = query?.match(/document=(\d+)/)
    const code = match?.[1] ?? ''
    return DOC_MAP[code] || code
  }

  const displayedRows = useMemo(() => {
    if (!order) return rows

    return [...rows].sort((a, b) => {
      const aVal = a[order.field]
      const bVal = b[order.field]

      if (order.field === 'fecha') {
        const dateA = aVal ? new Date(aVal) : new Date(0)
        const dateB = bVal ? new Date(bVal) : new Date(0)
        return order.direction === 'asc' ? dateA - dateB : dateB - dateA
      }

      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return order.direction === 'asc' ? aVal - bVal : bVal - aVal
      }

      const strA = aVal ? aVal.toString().toLowerCase() : ''
      const strB = bVal ? bVal.toString().toLowerCase() : ''
      if (strA < strB) return order.direction === 'asc' ? -1 : 1
      if (strA > strB) return order.direction === 'asc' ? 1 : -1
      return 0
    })
  }, [rows, order])

  const groupedRows = useMemo(() => {
    return displayedRows.map((row, idx, arr) => {
      const prev = arr[idx - 1] || {}
      const isNewGroup = idx === 0 || row.comprobante !== prev.comprobante
      return {
        ...row,
        showComp: isNewGroup,
        showFecha: isNewGroup || row.fecha !== prev.fecha,
        showTipo: isNewGroup || row.tipoComprobante?.name !== prev.tipoComprobante?.name,
        showRut: isNewGroup || row.informacion?.rutEmpresa !== prev.informacion?.rutEmpresa,
        showRazon: isNewGroup || row.informacion?.razonSocial !== prev.informacion?.razonSocial,
        showNumero: isNewGroup || row.numero !== prev.numero,
        showGlosa: isNewGroup || row.glosa !== prev.glosa
      }
    })
  }, [displayedRows])

  if (!rows?.length) {
    return <Text className='grid place-items-center py-12 text-muted font-sans'>No hay datos</Text>
  }

  if (isMobile) {
    const renderMetric = (label, value) => (
      <div className='rounded-lg bg-surface/70 border border-border px-2 py-2 min-w-0'>
        <div className='text-[11px] text-muted'>{label}</div>
        <div className='text-sm font-semibold text-text break-words'>{value}</div>
      </div>
    )

    return (
      <div className={clsx('space-y-3 font-sans text-text', className)}>
        {groupedRows.map((row, i) => (
          <div key={`mob-${i}`} className='rounded-2xl border border-border bg-surface p-3'>
            <div className='text-xs font-semibold text-text'>
              {row.comprobante || 'Comprobante'}
            </div>
            <div className='mt-1 text-[11px] text-muted'>
              {row.fecha ? formatDMY(row.fecha) : ''} · {row.tipoComprobante?.name || ''}
            </div>
            <div className='mt-1 text-[11px] text-muted'>
              {row.informacion?.rutEmpresa || ''} {row.informacion?.razonSocial || ''}
            </div>
            <div className='mt-2 text-[11px] text-muted'>
              {getDocName(row.process?.query)} · {row.numero || ''}
            </div>
            <div className='mt-2 text-[11px] text-muted'>
              {row.glosa || ''}
            </div>
            <div className='mt-2 text-[11px] text-muted'>
              {row.cuentaContable?.nombreCuenta || ''}
            </div>
            <div className='mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2'>
              {renderMetric('Debe', formatCurrencySinSignoPeso(row.debe))}
              {renderMetric('Haber', formatCurrencySinSignoPeso(row.haber))}
            </div>
          </div>
        ))}

        {hasNextPage && (
          <div ref={lastElementRef} className='flex justify-center py-4 flex-shrink-0'>
            {isFetchingNextPage ? (
              <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
            ) : (
              <button
                onClick={onLoadMore}
                className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
              >
                Cargar más
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className='border border-border rounded overflow-hidden bg-surface font-sans text-[11px] sm:text-xs text-text'>
      <div className='overflow-x-auto'>
        <div className={`max-h-[60vh] overflow-y-auto ${className}`}>
          <table className='table-report-auto min-w-[1120px]' style={{ tableLayout: 'auto' }}>
            <thead className='sticky top-0 bg-surface z-10'>
              <tr>
                {cols.map((col, i) => {
                  const cfg = findColConfig(col)
                  const isRazon = col === 'Razon Social'
                  const isGlosa = col === 'Glosa'
                  const isCuenta = col === 'Cuenta'
                  const widthClass = isRazon || isGlosa ? 'w-[170px]' : isCuenta ? 'w-[190px]' : ''
                  return (
                    <th
                      key={i}
                      className={clsx(
                        'table-report-th text-center align-middle',
                        widthClass,
                        cfg && 'cursor-pointer select-none'
                      )}
                      onClick={() => cfg && handleSort(cfg)}
                    >
                      <div className='flex items-center justify-center'>
                        <span>{col}</span>
                        {cfg && (
                          <KeyboardArrowDownIcon
                            className={clsx('ml-1 transition-transform', {
                              'opacity-40': !order || order.col !== col,
                              'rotate-180': order && order.col === col && order.direction === 'desc'
                            })}
                            fontSize='small'
                          />
                        )}
                      </div>
                    </th>
                  )
                })}
              </tr>
            </thead>
            <tbody>
              {groupedRows.map((row, i) => {
                // detectamos si el siguiente inicio de grupo está en la fila i+1
                const next = groupedRows[i + 1]
                const isEndOfGroup = !next || next.showComp || next.showFecha || next.showTipo
                return (
                  <Fragment key={i}>
                    <tr className='table-report-row'>
                      {/* Fecha */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showFecha ? formatDMY(row.fecha) : ''}</Text>
                      </td>
                      {/* Comprobante */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showComp ? row.comprobante : ''}</Text>
                      </td>
                      {/*Tipo */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showTipo ? row.tipoComprobante?.name : ''}</Text>
                      </td>
                      {/* Rut */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showRut ? row.informacion?.rutEmpresa : ''}</Text>
                      </td>
                      {/* Razón Social */}
                      <td
                        className='
                        px-3 py-2 align-top
                        whitespace-normal break-words
                        w-48 text-center
                      '
                      >
                        <Text>{row.showRazon ? row.informacion?.razonSocial : ''}</Text>
                      </td>
                      {/* Glosa */}
                      <td
                        className='
                        px-3 py-2 align-top
                        whitespace-normal break-words
                        w-48 text-center
                      '
                      >
                        <Text>{row.showGlosa ? row.glosa : ''}</Text>
                      </td>
                      {/* Documento */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showNumero ? getDocName(row.process?.query) : ''}</Text>
                      </td>
                      {/* Folio */}
                      <td className='table-report-td text-center'>
                        <Text>{row.showNumero ? row.numero : ''}</Text>
                      </td>
                      {/* Cuenta */}
                      <td className='px-3 py-2 align-top whitespace-normal break-words leading-tight w-[190px] text-center'>
                        <Text>{row.cuentaContable?.nombreCuenta}</Text>
                      </td>
                      {/* Debe */}
                      <td className='table-report-td text-right'>
                        <Text>{formatCurrencySinSignoPeso(row.debe)}</Text>
                      </td>
                      {/* Haber */}
                      <td className='table-report-td text-right'>
                        <Text>{formatCurrencySinSignoPeso(row.haber)}</Text>
                      </td>
                    </tr>

                    {isEndOfGroup && (
                      <tr>
                        <td colSpan={cols.length} className='p-0'>
                          <div className='h-px bg-border my-1' />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                )
              })}
            </tbody>
          </table>
          {hasNextPage && (
            <div ref={lastElementRef} className='flex justify-center py-4 flex-shrink-0'>
              {isFetchingNextPage ? (
                <Lottie animationData={LoaderAdm} loop style={{ width: 100, height: 100 }} />
              ) : (
                <button
                  onClick={onLoadMore}
                  className='px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors'
                >
                  Cargar más
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
